import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { money, qty, useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { ChargeType, Plan, PlanCharge } from '../types';

/** Что означает каждый тип компонента и какие поля он требует. */
const CHARGE_TYPES: Array<{ value: ChargeType; label: string; hint: string; needsMeter: boolean }> = [
  { value: 'RECURRING', label: 'Абонентская плата', hint: 'фиксированная сумма за период', needsMeter: false },
  { value: 'PER_UNIT', label: 'Цена за единицу', hint: 'плата за каждую единицу сверх включённого объёма', needsMeter: true },
  { value: 'TIERED', label: 'Ступени', hint: 'каждая ступень тарифицирует только свою часть объёма', needsMeter: true },
  { value: 'VOLUME', label: 'Объёмная цена', hint: 'весь объём по ставке достигнутой ступени', needsMeter: true },
  { value: 'PACKAGE', label: 'Пакеты', hint: 'блоки по N единиц, начатый блок оплачивается целиком', needsMeter: true },
  { value: 'ONE_TIME', label: 'Разовый платёж', hint: 'списывается один раз, на первом счёте по договору', needsMeter: false },
];

const emptyCharge = {
  code: '',
  name: '',
  chargeType: 'RECURRING' as ChargeType,
  meterId: '',
  amountRub: '',
  unitAmountRub: '',
  packageSize: '',
  packageAmountRub: '',
  includedUnits: '',
  scaleWithQuantity: false,
  tiersJson: '',
};

/**
 * Конструктор тарифов. План — набор компонентов; из них складывается любая схема, которая нам
 * встречалась: абонплата за место, включённый объём с ценой сверх лимита, ступени, пакеты.
 */
export function PlansPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [chargeFor, setChargeFor] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState({ code: '', name: '', description: '', billingPeriod: 'MONTH' });
  const [charge, setCharge] = useState({ ...emptyCharge });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['plans', tenantId] });
  const rub = (value: string): number | undefined =>
    value === '' ? undefined : Math.round(Number(value) * 100);

  const createPlan = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/plans`, planForm),
    onSuccess: () => {
      refresh();
      setCreatingPlan(false);
      setPlanForm({ code: '', name: '', description: '', billingPeriod: 'MONTH' });
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const addCharge = useMutation({
    mutationFn: () =>
      api.post(`/tenants/${tenantId}/plans/${chargeFor!.id}/charges`, {
        code: charge.code,
        name: charge.name,
        chargeType: charge.chargeType,
        meterId: charge.meterId || undefined,
        amountKopecks: rub(charge.amountRub),
        unitAmountKopecks: rub(charge.unitAmountRub),
        packageSize: charge.packageSize === '' ? undefined : Number(charge.packageSize),
        packageAmountKopecks: rub(charge.packageAmountRub),
        includedUnits: charge.includedUnits === '' ? 0 : Number(charge.includedUnits),
        scaleWithQuantity: charge.scaleWithQuantity,
        tiersJson: charge.tiersJson || undefined,
        sortOrder: chargeFor!.charges.length + 1,
      }),
    onSuccess: () => {
      refresh();
      setChargeFor(null);
      setCharge({ ...emptyCharge });
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const removeCharge = useMutation({
    mutationFn: (args: { planId: string; chargeId: string }) =>
      api.del(`/tenants/${tenantId}/plans/${args.planId}/charges/${args.chargeId}`),
    onSuccess: refresh,
  });

  const selected = CHARGE_TYPES.find((t) => t.value === charge.chargeType)!;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Тарифы</h1>
          <div className="muted small">из чего складывается счёт</div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => { setError(null); setCreatingPlan(true); }}>
          <Icon name="plus" />
          Новый тариф
        </button>
      </div>

      {catalog.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : catalog.plans.length === 0 ? (
        <div className="card">
          <div className="state-title">Тарифов пока нет</div>
          <p className="muted">
            Тариф — набор компонентов: абонплата, цена за единицу, ступени, пакеты и разовые платежи
            в любой комбинации.
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreatingPlan(true)}>
              <Icon name="plus" />
              Создать тариф
            </button>
          </div>
        </div>
      ) : (
        <div className="plan-list">
          {catalog.plans.map((plan) => (
            <section className="card" key={plan.id}>
              <div className="plan-head">
                <div>
                  <h2>{plan.name}</h2>
                  <div className="muted small">
                    <span className="plan-code">{plan.code}</span> · период: {periodLabel(plan.billingPeriod)} ·{' '}
                    {plan.currency}
                    {!plan.active && ' · выключен'}
                  </div>
                  {plan.description && <p className="muted small">{plan.description}</p>}
                </div>
                <div className="spacer" />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setError(null); setChargeFor(plan); }}
                >
                  <Icon name="plus" />
                  Компонент
                </button>
              </div>

              {plan.charges.length === 0 ? (
                <div className="state">В тарифе нет ни одного компонента — счёт по нему будет пустым.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Компонент</th>
                      <th>Тип</th>
                      <th>Счётчик</th>
                      <th>Условия</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {plan.charges.map((c) => (
                      <tr key={c.id}>
                        <td>
                          {c.name}
                          <div className="muted small">
                            <code>{c.code}</code>
                          </div>
                        </td>
                        <td>{CHARGE_TYPES.find((t) => t.value === c.chargeType)?.label ?? c.chargeType}</td>
                        <td className="muted small">
                          {c.meterId ? catalog.meterById.get(c.meterId)?.name ?? '—' : '—'}
                        </td>
                        <td>
                          <ChargeTerms
                            charge={c}
                            unit={c.meterId ? catalog.meterById.get(c.meterId)?.unit : undefined}
                          />
                        </td>
                        <td className="num">
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => removeCharge.mutate({ planId: plan.id, chargeId: c.id })}
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ))}
        </div>
      )}

      {creatingPlan && (
        <Modal title="Новый тариф" onClose={() => setCreatingPlan(false)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="plan-code">
                Код
              </label>
              <input
                id="plan-code"
                className="input"
                value={planForm.code}
                onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="plan-period">
                Период
              </label>
              <select
                id="plan-period"
                className="input"
                value={planForm.billingPeriod}
                onChange={(e) => setPlanForm({ ...planForm, billingPeriod: e.target.value })}
              >
                <option value="MONTH">месяц</option>
                <option value="WEEK">неделя</option>
                <option value="DAY">день</option>
                <option value="YEAR">год</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="plan-name">
              Название
            </label>
            <input
              id="plan-name"
              className="input"
              value={planForm.name}
              onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="plan-desc">
              Описание
            </label>
            <input
              id="plan-desc"
              className="input"
              value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCreatingPlan(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!planForm.code || !planForm.name || createPlan.isPending}
              onClick={() => { setError(null); createPlan.mutate(); }}
            >
              Создать
            </button>
          </div>
        </Modal>
      )}

      {chargeFor && (
        <Modal title={`Компонент тарифа «${chargeFor.name}»`} onClose={() => setChargeFor(null)}>
          <div className="form-group">
            <label className="form-label" htmlFor="charge-type">
              Тип
            </label>
            <select
              id="charge-type"
              className="input"
              value={charge.chargeType}
              onChange={(e) => setCharge({ ...charge, chargeType: e.target.value as ChargeType })}
            >
              {CHARGE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <div className="form-hint">{selected.hint}</div>
          </div>

          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="charge-code">
                Код
              </label>
              <input
                id="charge-code"
                className="input"
                value={charge.code}
                onChange={(e) => setCharge({ ...charge, code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="charge-name">
                Название в счёте
              </label>
              <input
                id="charge-name"
                className="input"
                value={charge.name}
                onChange={(e) => setCharge({ ...charge, name: e.target.value })}
              />
            </div>
          </div>

          {selected.needsMeter && (
            <div className="form-group">
              <label className="form-label" htmlFor="charge-meter">
                Счётчик
              </label>
              <select
                id="charge-meter"
                className="input"
                value={charge.meterId}
                onChange={(e) => setCharge({ ...charge, meterId: e.target.value })}
              >
                <option value="">— выберите —</option>
                {catalog.meters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.unit})
                  </option>
                ))}
              </select>
            </div>
          )}

          {(charge.chargeType === 'RECURRING' || charge.chargeType === 'ONE_TIME') && (
            <div className="form-group">
              <label className="form-label" htmlFor="charge-amount">
                Сумма, ₽
              </label>
              <input
                id="charge-amount"
                className="input"
                type="number"
                value={charge.amountRub}
                onChange={(e) => setCharge({ ...charge, amountRub: e.target.value })}
              />
            </div>
          )}

          {charge.chargeType === 'PER_UNIT' && (
            <div className="form-group">
              <label className="form-label" htmlFor="charge-unit">
                Цена за единицу, ₽
              </label>
              <input
                id="charge-unit"
                className="input"
                type="number"
                step="0.01"
                value={charge.unitAmountRub}
                onChange={(e) => setCharge({ ...charge, unitAmountRub: e.target.value })}
              />
            </div>
          )}

          {charge.chargeType === 'PACKAGE' && (
            <div className="row">
              <div className="form-group">
                <label className="form-label" htmlFor="charge-pkg-size">
                  Единиц в пакете
                </label>
                <input
                  id="charge-pkg-size"
                  className="input"
                  type="number"
                  value={charge.packageSize}
                  onChange={(e) => setCharge({ ...charge, packageSize: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="charge-pkg-amount">
                  Цена пакета, ₽
                </label>
                <input
                  id="charge-pkg-amount"
                  className="input"
                  type="number"
                  value={charge.packageAmountRub}
                  onChange={(e) => setCharge({ ...charge, packageAmountRub: e.target.value })}
                />
              </div>
            </div>
          )}

          {(charge.chargeType === 'TIERED' || charge.chargeType === 'VOLUME') && (
            <div className="form-group">
              <label className="form-label" htmlFor="charge-tiers">
                Ступени
              </label>
              <textarea
                id="charge-tiers"
                className="input"
                rows={3}
                value={charge.tiersJson}
                onChange={(e) => setCharge({ ...charge, tiersJson: e.target.value })}
                placeholder='[{"upTo":200,"unitAmountKopecks":0},{"upTo":null,"unitAmountKopecks":4500}]'
              />
              <div className="form-hint">
                Список ступеней от меньшей к большей; у последней <code>upTo: null</code> — «и всё
                сверх того». Цены в копейках.
              </div>
            </div>
          )}

          {selected.needsMeter && (
            <div className="form-group">
              <label className="form-label" htmlFor="charge-included">
                Включено в тариф
              </label>
              <input
                id="charge-included"
                className="input"
                type="number"
                value={charge.includedUnits}
                onChange={(e) => setCharge({ ...charge, includedUnits: e.target.value })}
                placeholder="0"
              />
              <div className="form-hint">Единиц, которые не тарифицируются.</div>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={charge.scaleWithQuantity}
                onChange={(e) => setCharge({ ...charge, scaleWithQuantity: e.target.checked })}
              />
              <span>
                Умножать на количество по договору
                <span className="form-hint">
                  Терминалы, места, устройства: и сумма, и включённый объём умножаются на количество из договора.
                </span>
              </span>
            </label>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setChargeFor(null)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!charge.code || !charge.name || addCharge.isPending}
              onClick={() => { setError(null); addCharge.mutate(); }}
            >
              Добавить
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/** Человеческое описание условий компонента — чтобы тариф читался, а не расшифровывался. */
function ChargeTerms({ charge, unit }: { charge: PlanCharge; unit?: string }) {
  const scale = charge.scaleWithQuantity ? ' × количество' : '';
  const included =
    charge.includedUnits > 0
      ? `включено ${qty(charge.includedUnits)} ${unit ?? ''}${charge.scaleWithQuantity ? ' на единицу' : ''}`
      : '';

  switch (charge.chargeType) {
    case 'RECURRING':
    case 'ONE_TIME':
      return (
        <>
          {money(charge.amountKopecks ?? 0)} ₽{scale}
        </>
      );
    case 'PER_UNIT':
      return (
        <>
          {money(charge.unitAmountKopecks ?? 0)} ₽ за {unit ?? 'единицу'}
          {included && <div className="muted small">{included}</div>}
        </>
      );
    case 'PACKAGE':
      return (
        <>
          {money(charge.packageAmountKopecks ?? 0)} ₽ за {charge.packageSize} {unit ?? ''}
          {included && <div className="muted small">{included}</div>}
        </>
      );
    case 'TIERED':
    case 'VOLUME':
      return <Tiers json={charge.tiersJson} included={included} />;
    default:
      return <>—</>;
  }
}

function Tiers({ json, included }: { json: string | null; included: string }) {
  if (!json) return <>—</>;
  try {
    const tiers = JSON.parse(json) as Array<{ upTo: number | null; unitAmountKopecks: number }>;
    return (
      <>
        {tiers.map((t, i) => (
          <div key={i} className="small">
            {t.upTo === null ? 'свыше' : `до ${qty(t.upTo)}`}: {money(t.unitAmountKopecks)} ₽
          </div>
        ))}
        {included && <div className="muted small">{included}</div>}
      </>
    );
  } catch {
    return <span className="form-error">ступени заданы неверно</span>;
  }
}

const periodLabel = (period: string): string =>
  ({ DAY: 'день', WEEK: 'неделя', MONTH: 'месяц', YEAR: 'год' })[period] ?? period;
